/**
 * Hook unifié pour la gestion des membres : filtres, recherche fuzzy, tri.
 */
import { useContext, useMemo, useState } from "react";
import Fuse from "fuse.js";
import { MembersContext } from "../contexts/MembersContext";

const FUSE_OPTIONS = {
  keys: ["prenom", "nom", "full_name", "email", "telephone", "ville", "departement", "role_eglise"],
  threshold: 0.35,
  ignoreLocation: true,
  minMatchCharLength: 2,
};

export function useMembers() {
  const ctx = useContext(MembersContext);
  if (!ctx) throw new Error("useMembers doit être utilisé à l'intérieur de <MembersProvider>");

  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    statut: "all",
    eglise_id: "all",
    departement: "all",
    genre: "all",
  });
  const [sortBy, setSortBy] = useState("nom"); // "nom" | "prenom" | "ville" | "statut"

  // ─── Index Fuse.js (recalculé seulement si la liste change) ───
  const fuse = useMemo(() => new Fuse(ctx.members, FUSE_OPTIONS), [ctx.members]);

  // ─── Liste filtrée + triée ───
  const filtered = useMemo(() => {
    let list = ctx.members;

    // Recherche fuzzy
    if (search.trim().length >= 2) {
      list = fuse.search(search.trim()).map((r) => r.item);
    }

    // Filtres
    if (filters.statut !== "all") {
      list = list.filter((m) => m.statut === filters.statut);
    }
    if (filters.eglise_id !== "all") {
      list = list.filter((m) => m.eglise_id === Number(filters.eglise_id));
    }
    if (filters.departement !== "all") {
      list = list.filter((m) => m.departement === filters.departement);
    }
    if (filters.genre !== "all") {
      list = list.filter((m) => m.genre === filters.genre);
    }

    // Tri
    list = [...list].sort((a, b) => {
      const av = (a[sortBy] || "").toString().toLowerCase();
      const bv = (b[sortBy] || "").toString().toLowerCase();
      return av.localeCompare(bv, "fr");
    });

    return list;
  }, [ctx.members, search, filters, sortBy, fuse]);

  // ─── Liste des départements uniques (pour le filtre) ───
  const departements = useMemo(() => {
    const set = new Set(ctx.members.map((m) => m.departement).filter(Boolean));
    return Array.from(set).sort();
  }, [ctx.members]);

  return {
    ...ctx,
    filtered,
    search,
    setSearch,
    filters,
    setFilters,
    sortBy,
    setSortBy,
    departements,
  };
}

export default useMembers;
